import React, { useEffect, useState } from "react";
import { BookOpenCheck, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { fmtDate, useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { Field } from "../components/fields";
import DateInput from "../components/DateInput";

const EMPTY = { version: "", date: new Date().toISOString().slice(0, 10), section: "", description: "", approver_name: "", approver_position: "" };

export default function ManualChanges() {
  const { t } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/compliance/manual-changes").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/compliance/manual-changes", form);
      toast.success(t("mc.saved"));
      setShowForm(false); setForm(EMPTY); load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6" data-testid="manual-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-3">
            <BookOpenCheck className="w-7 h-7 text-[#044CBC]" /> {t("mc.title")}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{t("mc.subtitle")}</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowForm(!showForm)} data-testid="manual-add-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C]">
            <Plus className="w-4 h-4" /> {t("mc.new")}
          </button>
        )}
      </div>

      {showForm && isAdmin && (
        <form onSubmit={submit} className="ec-card p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end" data-testid="manual-form">
          <Field label={t("mc.version")}><input className="ec-input" value={form.version} onChange={set("version")} required data-testid="mc-version" /></Field>
          <Field label={t("common.date")}><DateInput className="ec-input" value={form.date} onChange={set("date")} data-testid="mc-date" /></Field>
          <Field label={t("mc.section")}><input className="ec-input" value={form.section} onChange={set("section")} data-testid="mc-section" /></Field>
          <Field label={t("mc.description")} className="sm:col-span-2 lg:col-span-3"><textarea className="ec-input" rows={2} value={form.description} onChange={set("description")} data-testid="mc-description" /></Field>
          <Field label={t("mc.approver_name")}><input className="ec-input" value={form.approver_name} onChange={set("approver_name")} data-testid="mc-approver-name" /></Field>
          <Field label={t("mc.approver_position")}><input className="ec-input" value={form.approver_position} onChange={set("approver_position")} data-testid="mc-approver-position" /></Field>
          <button type="submit" disabled={busy} data-testid="mc-submit" className="px-4 py-2.5 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60 flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("common.save")}
          </button>
        </form>
      )}

      <div className="ec-card overflow-hidden" data-testid="manual-table">
        {items === null ? (
          <div className="p-10 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400" data-testid="manual-empty">{t("h.empty")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="bg-[#16335F] text-white text-left">
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("mc.version")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("common.date")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("mc.section")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("mc.description")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("mc.approver_name")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`mc-row-${m.version}`}>
                    <td className="px-4 py-2.5 font-mono font-bold text-slate-800">v{m.version}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{m.date || fmtDate(m.created_at)}</td>
                    <td className="px-4 py-2.5 text-slate-700">{m.section}</td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs">{m.description}</td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs">{m.approver_name}{m.approver_position ? ` — ${m.approver_position}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
